import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translations } from '../translations';

const LanguageContext = createContext(null);

const SUPPORTED_LANGS = ['pt', 'en', 'fr', 'es'];
const LANG_LABELS = { pt: 'PT', en: 'EN', fr: 'FR', es: 'ES' };
const LANG_NAMES = { pt: 'Portugues', en: 'English', fr: 'Francais', es: 'Espanol' };
const STORAGE_KEY = 'papo_language';

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
    const browser = navigator.language?.slice(0, 2);
    if (SUPPORTED_LANGS.includes(browser)) return browser;
    return 'pt';
  });

  const setLanguage = useCallback((lang) => {
    if (SUPPORTED_LANGS.includes(lang)) {
      setLanguageState(lang);
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
      document.documentElement.setAttribute('data-theme-locale', lang === 'pt' ? 'pt' : 'pan');
    }
  }, []);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.setAttribute('data-theme-locale', language === 'pt' ? 'pt' : 'pan');
  }, [language]);

  const t = useCallback((key, params) => {
    const value = translations[language]?.[key] || translations['pt']?.[key] || key;
    if (!params) return value;
    return Object.entries(params).reduce((str, [k, v]) => str.replace(`{${k}}`, v), value);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, SUPPORTED_LANGS, LANG_LABELS, LANG_NAMES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useTranslation must be used within LanguageProvider');
  return ctx;
};
